import React, { FC, memo, useEffect, useState } from 'react';
import Stack from './Stack';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Modal from '@oberoninternal/travelbase-ds/components/layout/Modal';
import ModalHeader from '@oberoninternal/travelbase-ds/components/layout/ModalHeader';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { Box, Flex } from '@rebass/grid';
import styled from 'styled-components';
import { useTenantContext } from '../context/TenantContext';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import Anchor from './designsystem/Anchor';
import { Locale } from '../entities/Locale';
import { ContactInfo, ContactInfoByBrand } from '../constants/brandConfig';
import BookButton from './designsystem/BookButton';

interface Props {
    className?: string;
    rentalUnitName?: string;
    rentalUnitCode?: string;
    brand?: string;
    reserving?: boolean;
    bookingIsValid: boolean;
    onBookingReservation?: () => void;
    type: 'unit' | 'accommodation';
}

const getInfoLink = (info: ContactInfo, locale: Locale, rentalUnitName: string, rentalUnitCode: string) => {
    let { link } = info;

    if (info.queryParams) {
        const searchParams: { [key: string]: string } = {};

        Object.keys(info.queryParams[locale]).forEach(key => {
            const value = info.queryParams?.[locale]?.[key];
            if (value) {
                searchParams[key as string] = encodeURIComponent(
                    value.replace('{rentalUnitName}', `${rentalUnitName} ${`(${rentalUnitCode})`}`)
                );
            }
        });

        link += `?${decodeURI(new URLSearchParams(searchParams).toString())}`;
    }
    return link;
};

const PlannerHelp: FC<React.PropsWithChildren<Props>> = ({
    className,
    rentalUnitName,
    rentalUnitCode,
    brand,
    reserving,
    bookingIsValid,
    onBookingReservation,
    type,
}) => {
    const { onClose, open, onOpen } = useSesame();
    const { brandConfig } = useTenantContext();
    const [nativeContactInfo, setNativeContactInfo] = useState<ContactInfo[] | undefined>(undefined);
    useEffect(() => {
        if (brand && brandConfig.contact.useNativeContactInfo) {
            setNativeContactInfo(brandConfig?.contact?.contactInfoByBrand?.[brand as keyof ContactInfoByBrand]);
        }
    }, [brand, brandConfig.contact?.contactInfoByBrand, brandConfig.contact.useNativeContactInfo]);
    const intl = useIntl();
    return (
        <>
            <Stack
                variant="inline"
                spacing={4}
                alignItems="center"
                justifyContent={['center', null, 'space-between']}
                className={className}
                mt={[4, null, 0]}
                pb={[4, null, 0]}
                width={[1, 'auto']}
            >
                <StyledTextbutton className="lt-s" onClick={onOpen}>
                    <Anchor>
                        <FormattedMessage defaultMessage="Hulp bij het boeken?" />
                    </Anchor>
                    <Stack variant="inline">
                        {!brandConfig.contact?.useNativeContactInfo
                            ? brandConfig?.contact?.info?.map(info => (
                                  <Stack width="40px" key={info.link}>
                                      {info.icon}
                                  </Stack>
                              ))
                            : nativeContactInfo?.map(info => (
                                  <Stack width="40px" key={info.link}>
                                      {info.icon}
                                  </Stack>
                              ))}
                    </Stack>
                </StyledTextbutton>
                <Button className="gt-s" variant="outline" onClick={onOpen}>
                    <FormattedMessage defaultMessage="Contact opnemen" />
                </Button>
                <BookButton
                    submitting={reserving}
                    disabled={!bookingIsValid}
                    onClick={onBookingReservation}
                    data-cy="bookingButton"
                >
                    {type === 'unit' && bookingIsValid && <FormattedMessage defaultMessage="Reserveren" />}
                    {type === 'unit' && !bookingIsValid && <FormattedMessage defaultMessage="Kies datum" />}
                    {type === 'accommodation' && <FormattedMessage defaultMessage="Kies een verblijf" />}
                </BookButton>
            </Stack>
            <Modal onClose={onClose} open={open} variant="regular">
                <ModalHeader variant="small">
                    <FormattedMessage defaultMessage="Contact informatie" />
                </ModalHeader>
                {!brandConfig.contact?.useNativeContactInfo
                    ? brandConfig.contact?.info?.map(info => (
                          <ContactBlock
                              href={getInfoLink(
                                  info,
                                  intl.locale as Locale,
                                  rentalUnitName ?? '',
                                  rentalUnitCode ?? ''
                              )}
                              key={info.link}
                              target="_blank"
                          >
                              <StyledRow flexDirection="row">
                                  <StyledIconBox mr={[4, 3]}>{info.icon}</StyledIconBox>
                                  <Box>
                                      <Title>{info.name}</Title>
                                      <StyledBody>{info.content}</StyledBody>
                                  </Box>
                              </StyledRow>
                          </ContactBlock>
                      ))
                    : nativeContactInfo?.map(info => (
                          <ContactBlock
                              href={getInfoLink(
                                  info,
                                  intl.locale as Locale,
                                  rentalUnitName ?? '',
                                  rentalUnitCode ?? ''
                              )}
                              key={info.link}
                              target="_blank"
                          >
                              <StyledRow flexDirection="row">
                                  <StyledIconBox mr={[4, 3]}>{info.icon}</StyledIconBox>
                                  <Box>
                                      <Title>{info.name}</Title>
                                      <StyledBody>{info.content}</StyledBody>
                                  </Box>
                              </StyledRow>
                          </ContactBlock>
                      ))}
            </Modal>
        </>
    );
};

const StyledRow = styled(Flex)`
    padding: 24px;
`;

const StyledTextbutton = styled(Flex)`
    width: 100%;
    justify-content: space-between;
`;

const ContactBlock = styled.a`
    border: 1px solid ${({ theme }) => theme.colors.neutral['20']};

    display: flex;
    flex-direction: column;
    border-radius: 0.6rem;
    text-decoration: none;
    color: inherit;
    margin-top: 0.8rem;
    margin-bottom: 0.8rem;

    &:visited {
        text-decoration: none;
        color: inherit;
    }
`;
const StyledIconBox = styled(Box)`
    max-width: 3.2rem;
    svg {
        margin-right: 1.6rem;
        @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
            width: 3.2rem;
            height: 3.2rem;
        }

        width: 32px;
        height: 32px;
    }
`;

const StyledBody = styled(Body)`
    line-height: 1.5;
    margin-bottom: 0.8rem;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
        min-height: 5.6rem;
        margin-bottom: 1.6rem;
    }
`;
export default memo(PlannerHelp);
